import { useCustomerUpdateDetails, useSettings } from '@backpackjs/storefront';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AccountLayout from '../../layouts/AccountLayout';
import { Button, Spinner } from '../../snippets';

export function EditProfile({ customer }) {
  const {
    customerUpdateDetails,
    started,
    finished,
    errors,
    success,
    reset,
    resetTimer,
  } = useCustomerUpdateDetails();
  const { asPath } = useRouter();
  const settings = useSettings();
  const { menuItems } = { ...settings?.account?.menu };
  const heading = menuItems?.find(({ link }) => asPath.startsWith(link?.url))
    ?.link?.text;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    if (!customer) return;
    setFirstName(customer.firstName);
    setLastName(customer.lastName);
  }, [customer, finished]);

  const buttonText = useMemo(() => {
    if (finished) {
      resetTimer.current = setTimeout(() => {
        if (reset) reset();
        clearTimeout(resetTimer.current);
      }, 3000);
      return success ? 'Profile saved' : 'Failed';
    }
    return started ? 'Saving profile' : 'Save profile';
  }, [started, success, finished]);

  const handleUpdateCustomerDetails = useCallback(
    async (event) => {
      event.preventDefault();
      if (!customer || started) return;

      await customerUpdateDetails({
        acceptsMarketing: customer.acceptsMarketing,
        email: customer.email,
        firstName,
        lastName,
      });
    },
    [firstName, lastName, started, customer]
  );

  return (
    <AccountLayout>
      <div className="flex flex-col">
        <h1 className="text-title-h4 mb-8 md:mb-10">{heading}</h1>

        <form
          className="portal-form grid grid-cols-2 gap-3 rounded border border-border bg-white p-4 sm:p-6"
          onSubmit={handleUpdateCustomerDetails}
        >
          <label htmlFor="firstName" className="col-span-2 sm:col-span-1">
            <span className="input-label">First Name</span>
            <input
              className="input-text"
              name="firstName"
              onChange={({ target }) => setFirstName(target.value)}
              placeholder="First Name"
              required
              type="text"
              value={firstName}
            />
          </label>

          <label htmlFor="lastName" className="col-span-2 sm:col-span-1">
            <span className="input-label">Last Name</span>
            <input
              className="input-text"
              name="lastName"
              onChange={({ target }) => setLastName(target.value)}
              placeholder="Last Name"
              required
              type="text"
              value={lastName}
            />
          </label>

          <label htmlFor="email" className="col-span-2">
            <span className="input-label">Email</span>
            <input
              className="input-text text-mediumGray"
              disabled
              name="email"
              placeholder="Email"
              required
              type="email"
              value={customer?.email}
            />
          </label>

          <div className="col-span-2 flex justify-center">
            <Button
              theme="primary"
              variant="filled"
              aria-label="Save to update profile"
              className={started ? 'cursor-default' : ''}
              type="submit"
            >
              {started ? <Spinner /> : buttonText}
            </Button>
          </div>

          {errors?.length > 0 && (
            <ul className="col-span-2 mt-4 flex flex-col items-center gap-1">
              {errors.map((error, index) => {
                return (
                  <li key={index} className="text-center text-sm text-red-500">
                    {error}
                  </li>
                );
              })}
            </ul>
          )}
        </form>
      </div>
    </AccountLayout>
  );
}
